import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select } from '../ui/Form';
import { Save, Building2 } from 'lucide-react';
import { accountsApi } from '../../services/api/endpoints/accounts';
import { useAuthStore } from '../../stores/authStore';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import type { Account } from '../../types/accounting';
import type { FixedAsset, CreateFixedAssetPayload, DepreciationMethod } from '../../types/fixed-assets';
import { DEPRECIATION_METHOD_LABELS } from '../../types/fixed-assets';

interface FixedAssetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFixedAssetPayload) => Promise<void>;
  asset?: FixedAsset | null;
}

export function FixedAssetFormModal({ isOpen, onClose, onSubmit, asset }: FixedAssetFormModalProps) {
  const [loading, setLoading] = useState(false);
  const { organization } = useAuthStore();
  const { currentFiscalYear } = useFiscalYearStore();
  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    description: '',
    assetAccountId: '',
    depreciationAccountId: '',
    depreciationExpenseAccountId: '',
    acquisitionCost: '',
    residualValue: '0',
    depreciationMethod: 'linear' as DepreciationMethod,
    usefulLifeYears: '5',
    acquisitionDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Chargement ciblé des comptes par classe SYSCOHADA via l'API
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
  const [depreciationAccounts, setDepreciationAccounts] = useState<Account[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const loadAccountsByClass = async (classCode: string): Promise<Account[]> => {
      try {
        const response = await accountsApi.search('', { classCode, limit: 200, standard: 'SYSCOHADA' });
        if (response.success && response.data) {
          // handleResponse déplie l'enveloppe API: response.data = { results, ... }
          const data = response.data as any;
          const results = data?.results ?? data?.data?.results ?? [];
          return Array.isArray(results) ? results : [];
        }
      } catch (err) {
        console.error(`Erreur chargement comptes classe ${classCode}:`, err);
      }
      return [];
    };

    // Charger les 3 classes en parallèle
    Promise.all([
      loadAccountsByClass('2'),
      loadAccountsByClass('28'),
      loadAccountsByClass('68'),
    ]).then(([assets, depreciation, expenses]) => {
      setAssetAccounts(assets);
      // Filtrer classe 28 hors des comptes classe 2 pour éviter les doublons
      setDepreciationAccounts(depreciation.length > 0 ? depreciation : assets.filter(a => a.code?.startsWith('28')));
      setExpenseAccounts(expenses);
    });
  }, [isOpen]);

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name,
        reference: asset.reference || '',
        description: asset.description || '',
        assetAccountId: asset.assetAccountId,
        depreciationAccountId: asset.depreciationAccountId,
        depreciationExpenseAccountId: asset.depreciationExpenseAccountId,
        acquisitionCost: String(asset.acquisitionCost),
        residualValue: String(asset.residualValue),
        depreciationMethod: asset.depreciationMethod,
        usefulLifeYears: String(asset.usefulLifeYears),
        acquisitionDate: asset.acquisitionDate.split('T')[0],
      });
    }
  }, [asset]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.assetAccountId) newErrors.assetAccountId = 'Le compte d\'immobilisation est requis';
    if (!formData.depreciationAccountId) newErrors.depreciationAccountId = 'Le compte d\'amortissement est requis';
    if (!formData.depreciationExpenseAccountId) newErrors.depreciationExpenseAccountId = 'Le compte de dotation est requis';
    if (!formData.acquisitionCost || parseFloat(formData.acquisitionCost) <= 0) {
      newErrors.acquisitionCost = 'Le coût d\'acquisition doit être supérieur à 0';
    }
    if (!formData.usefulLifeYears || parseInt(formData.usefulLifeYears) <= 0) {
      newErrors.usefulLifeYears = 'La durée de vie doit être supérieure à 0';
    }
    if (!formData.acquisitionDate) newErrors.acquisitionDate = 'La date d\'acquisition est requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const selectedAssetAccount = assetAccounts.find(a => a.id === formData.assetAccountId);
      const selectedDepAccount = depreciationAccounts.find(a => a.id === formData.depreciationAccountId);
      const selectedExpAccount = expenseAccounts.find(a => a.id === formData.depreciationExpenseAccountId);

      await onSubmit({
        name: formData.name.trim(),
        reference: formData.reference.trim() || undefined,
        description: formData.description.trim() || undefined,
        assetAccountId: formData.assetAccountId,
        assetAccountCode: selectedAssetAccount?.code,
        depreciationAccountId: formData.depreciationAccountId,
        depreciationAccountCode: selectedDepAccount?.code,
        depreciationExpenseAccountId: formData.depreciationExpenseAccountId,
        depreciationExpenseAccountCode: selectedExpAccount?.code,
        acquisitionCost: parseFloat(formData.acquisitionCost),
        residualValue: parseFloat(formData.residualValue) || 0,
        depreciationMethod: formData.depreciationMethod,
        usefulLifeYears: parseInt(formData.usefulLifeYears),
        acquisitionDate: formData.acquisitionDate,
        fiscalYearId: currentFiscalYear?.id || '',
        companyId: organization?.id || '',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const depreciationMethodOptions = Object.entries(DEPRECIATION_METHOD_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const toSelectOptions = (accs: Account[]) =>
    Array.isArray(accs) ? accs.map(a => ({ value: a.id, label: `${a.code} — ${a.name}` })) : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={asset ? 'Modifier l\'immobilisation' : 'Nouvelle immobilisation'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Informations générales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nom de l'immobilisation" error={errors.name} required>
              <Input
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Ex: Véhicule Toyota Hilux"
                error={errors.name}
              />
            </FormField>
            <FormField label="Référence interne">
              <Input
                value={formData.reference}
                onChange={(e) => updateField('reference', e.target.value)}
                placeholder="Ex: IMM-2024-001"
              />
            </FormField>
          </div>
          <FormField label="Description">
            <textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Description détaillée de l'immobilisation"
              rows={2}
              className="form-textarea w-full"
            />
          </FormField>
        </div>

        {/* Comptes SYSCOHADA */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Comptes comptables (SYSCOHADA)</h3>
          <FormField label="Compte d'immobilisation (classe 2)" error={errors.assetAccountId} required>
            <Select
              value={formData.assetAccountId}
              onChange={(e) => updateField('assetAccountId', e.target.value)}
              options={toSelectOptions(assetAccounts)}
              error={errors.assetAccountId}
            />
          </FormField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Compte d'amortissement (28x)" error={errors.depreciationAccountId} required>
              <Select
                value={formData.depreciationAccountId}
                onChange={(e) => updateField('depreciationAccountId', e.target.value)}
                options={toSelectOptions(depreciationAccounts)}
                error={errors.depreciationAccountId}
              />
            </FormField>
            <FormField label="Compte de dotation (68x)" error={errors.depreciationExpenseAccountId} required>
              <Select
                value={formData.depreciationExpenseAccountId}
                onChange={(e) => updateField('depreciationExpenseAccountId', e.target.value)}
                options={toSelectOptions(expenseAccounts)}
                error={errors.depreciationExpenseAccountId}
              />
            </FormField>
          </div>
        </div>

        {/* Données financières */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Données financières</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Coût d'acquisition" error={errors.acquisitionCost} required>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.acquisitionCost}
                onChange={(e) => updateField('acquisitionCost', e.target.value)}
                placeholder="0.00"
                error={errors.acquisitionCost}
              />
            </FormField>
            <FormField label="Valeur résiduelle" hint="Valeur estimée en fin de vie (généralement 0 en SYSCOHADA)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.residualValue}
                onChange={(e) => updateField('residualValue', e.target.value)}
                placeholder="0.00"
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Méthode d'amortissement" required>
              <Select
                value={formData.depreciationMethod}
                onChange={(e) => updateField('depreciationMethod', e.target.value)}
                options={depreciationMethodOptions}
              />
            </FormField>
            <FormField label="Durée de vie (années)" error={errors.usefulLifeYears} required>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.usefulLifeYears}
                onChange={(e) => updateField('usefulLifeYears', e.target.value)}
                error={errors.usefulLifeYears}
              />
            </FormField>
            <FormField label="Date d'acquisition" error={errors.acquisitionDate} required>
              <Input
                type="date"
                value={formData.acquisitionDate}
                onChange={(e) => updateField('acquisitionDate', e.target.value)}
                error={errors.acquisitionDate}
              />
            </FormField>
          </div>
        </div>

        {/* Aperçu d'amortissement annuel */}
        {formData.acquisitionCost && formData.usefulLifeYears && (
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-muted-foreground">
              Amortissement annuel estimé (linéaire) :{' '}
              <span className="font-semibold text-foreground">
                {(Math.round(((parseFloat(formData.acquisitionCost) - parseFloat(formData.residualValue || '0')) / Math.max(1, parseInt(formData.usefulLifeYears) || 1)) * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" icon={Save} isLoading={loading}>
            {asset ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
